// Sidebar 折りたたみコントローラ
//
// md+ (>=768px): sidebar は常時表示 (固定 left)
// md-:         hamburger ボタンでトグル、 背後に backdrop、 リンク click で自動 close
//
// HTML:
//   <div data-controller="sidebar">
//     <button data-action="sidebar#toggle">menu</button>     <!-- topbar 内 -->
//     <aside data-sidebar-target="aside" class="-translate-x-full md:translate-x-0">
//     <div   data-sidebar-target="backdrop" data-action="click->sidebar#close">

import { Controller } from "@hotwired/stimulus"

const MD_BREAKPOINT = 768
const WIDTH_KEY = "akebono:sidebarWidth"
const MIN_WIDTH = 200
const MAX_WIDTH = 520
const DEFAULT_WIDTH = 256

export default class extends Controller {
  static targets = ["aside", "backdrop", "handle"]

  connect() {
    // 画面リサイズで md+ になったら強制的に open 状態にリセット
    this._onResize = this._onResize.bind(this)
    window.addEventListener("resize", this._onResize)
    // 内部 nav リンク click で auto-close (mobile のみ)
    this._onAsideClick = this._onAsideClick.bind(this)
    if (this.hasAsideTarget) {
      this.asideTarget.addEventListener("click", this._onAsideClick)
    }
    // リサイズ用ハンドラ
    this._onPointerMove = this._onPointerMove.bind(this)
    this._onPointerUp = this._onPointerUp.bind(this)
  }

  disconnect() {
    window.removeEventListener("resize", this._onResize)
    if (this.hasAsideTarget) {
      this.asideTarget.removeEventListener("click", this._onAsideClick)
    }
    this._stopResize()
  }

  // ── サイドバー幅リサイズ ──────────────────────────
  startResize(e) {
    if (window.innerWidth < MD_BREAKPOINT) return
    e.preventDefault()
    document.documentElement.classList.add("akebono-resizing")
    window.addEventListener("pointermove", this._onPointerMove)
    window.addEventListener("pointerup", this._onPointerUp)
  }

  _onPointerMove(e) {
    const w = this._clamp(e.clientX)
    document.documentElement.style.setProperty("--akebono-sidebar-w", `${w}px`)
  }

  _onPointerUp() {
    const w = this._currentWidth()
    try { localStorage.setItem(WIDTH_KEY, String(w)) } catch (_) {}
    this._stopResize()
  }

  _stopResize() {
    document.documentElement.classList.remove("akebono-resizing")
    window.removeEventListener("pointermove", this._onPointerMove)
    window.removeEventListener("pointerup", this._onPointerUp)
  }

  resetWidth(e) {
    e?.preventDefault?.()
    document.documentElement.style.setProperty("--akebono-sidebar-w", `${DEFAULT_WIDTH}px`)
    try { localStorage.setItem(WIDTH_KEY, String(DEFAULT_WIDTH)) } catch (_) {}
  }

  _clamp(px) {
    return Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Math.round(px)))
  }

  _currentWidth() {
    const v = parseInt(
      getComputedStyle(document.documentElement).getPropertyValue("--akebono-sidebar-w"),
      10
    )
    return Number.isFinite(v) ? v : DEFAULT_WIDTH
  }

  toggle(e) {
    e?.preventDefault?.()
    if (!this.hasAsideTarget) return
    const isOpen = !this.asideTarget.classList.contains("-translate-x-full")
    isOpen ? this.close() : this.open()
  }

  open() {
    if (!this.hasAsideTarget) return
    this.asideTarget.classList.remove("-translate-x-full")
    if (this.hasBackdropTarget) this.backdropTarget.classList.remove("hidden")
    document.documentElement.style.overflow = "hidden"
  }

  close() {
    if (!this.hasAsideTarget) return
    // md+ は close 動作させない (CSS で固定だが念のため)
    if (window.innerWidth >= MD_BREAKPOINT) return
    this.asideTarget.classList.add("-translate-x-full")
    if (this.hasBackdropTarget) this.backdropTarget.classList.add("hidden")
    document.documentElement.style.overflow = ""
  }

  _onResize() {
    if (window.innerWidth >= MD_BREAKPOINT) {
      // 大画面に拡げられたら mobile-only state を解除
      if (this.hasBackdropTarget) this.backdropTarget.classList.add("hidden")
      document.documentElement.style.overflow = ""
    }
  }

  _onAsideClick(e) {
    // ナビゲーションリンクをクリックしたら mobile では自動 close
    if (window.innerWidth >= MD_BREAKPOINT) return
    const a = e.target.closest("a")
    if (a && a.getAttribute("href")) this.close()
  }
}
